import { LabeledSegment, MultiPolygonGeometry, Point, Polygon, Segment, SegmentFeature } from "common-lib";
import simplify from "simplify-js";

function toContoursDbFormat(segments: LabeledSegment[]): string {
  const stripped: LabeledSegment[] = segments.map(({ id, label, polygons }) => ({
    id,
    label,
    polygons,
  }));
  return JSON.stringify(stripped);
}

function fromContoursDbFormat(raw: string | null): LabeledSegment[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as SegmentFeature[];
    if (!Array.isArray(parsed)) return [];
    return fromSegmentFeatures(parsed);
  } catch {
    return [];
  }
}

function pointInPolygon(x: number, y: number, ring: Polygon): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

// 세그먼트(여러 폴리곤/링) 중 하나라도 hit이면 true
function segmentHit(x: number, y: number, polygons: Segment): boolean {
  return polygons.some((ring) => pointInPolygon(x, y, ring));
}

function simplifyRing(ring: Point[], tolerance: number): Point[] {
  const points = ring.map(([x, y]) => ({ x, y }));
  const simplified = simplify(points, tolerance, true); // highQuality=true
  return simplified.map((p) => [p.x, p.y] as Point);
}

function simplifySegment(polygons: Segment, tolerance: number): Segment {
  return polygons.map((ring) => {
    const simplified = simplifyRing(ring, tolerance);
    // 단순화 후 3개 미만으로 줄면 폴리곤이 깨지므로 원본 유지
    return simplified.length >= 3 ? simplified : ring;
  });
}

function toSegmentFeatures(segments: LabeledSegment[], tolerance = 1.5): SegmentFeature[] {
  return segments.map((s) => ({
    geometry: {
      type: "MultiPolygon",
      coordinates: simplifySegment(s.polygons, tolerance).map((ring) => [ring]),
    },
    properties: { id: s.id, label: s.label },
  }));
}

function fromSegmentFeatures(features: SegmentFeature[]): LabeledSegment[] {
  return features
    .filter((f): f is SegmentFeature & { geometry: MultiPolygonGeometry } => f.geometry.type === "MultiPolygon")
    .map((f) => ({
      id: f.properties.id,
      label: f.properties.label,
      // MultiPolygon의 각 폴리곤에서 첫 번째 링(외곽선)만 취함
      // 구멍(hole)은 현재 앱의 Segment 구조가 지원하지 않으므로 무시
      polygons: f.geometry.coordinates.map((polygon) => polygon[0]),
    }));
}

export { toContoursDbFormat, fromContoursDbFormat, toSegmentFeatures, fromSegmentFeatures, simplifySegment, simplifyRing, segmentHit };
